import { Column } from '@ant-design/plots'
import { Empty, Spin } from 'antd'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useDashboardFilters } from '@contexts'
import { useMonthlyComplianceByZone } from '@hooks'

import { ChartEmptyWrap, ChartTitle } from './styled'

const PLAN_BAR_COLOR = '#F59E0B'
const REAL_BAR_COLOR = '#6B7280'

type ZoneDatum = {
  zone: string
  plan: number
  real: number
  compliance: number
}

function legendKey(datum: unknown): string {
  if (typeof datum === 'string') return datum
  if (datum && typeof datum === 'object') {
    const o = datum as Record<string, unknown>
    if (typeof o.value === 'string') return o.value
    if (typeof o.label === 'string') return o.label
    if (typeof o.id === 'string') return o.id
  }
  return ''
}

function formatMonthYearLabel(d: dayjs.Dayjs) {
  const raw = d.format('MMM YYYY')
  return raw.charAt(0).toLocaleUpperCase('es') + raw.slice(1)
}

export const MonthlyComplianceByZoneChart = () => {
  const { t } = useTranslation()
  const { selectedMonth } = useDashboardFilters()
  const { data: apiData, isLoading } = useMonthlyComplianceByZone()

  const raw: ZoneDatum[] = useMemo(
    () =>
      apiData?.zones?.map(row => ({
        zone: row.zone,
        plan: row.plan,
        real: row.real,
        compliance: row.compliance,
      })) ?? [],
    [apiData?.zones],
  )

  const byZone: Record<string, ZoneDatum | undefined> = useMemo(
    () => Object.fromEntries(raw.map(d => [d.zone, d])),
    [raw],
  )

  const hasData = raw.some(d => d.plan > 0 || d.real > 0)

  const data = raw.flatMap(({ zone, plan, real }) => [
    { zone, type: 'plan', value: plan },
    { zone, type: 'real', value: real },
  ])

  const config = useMemo(
    () => ({
      data,
      padding: [12, 16, 100, 16],
      xField: 'zone',
      yField: 'value',
      colorField: 'type',
      scale: {
        color: {
          domain: ['plan', 'real'],
          range: [PLAN_BAR_COLOR, REAL_BAR_COLOR],
        },
      },
      legend: {
        color: {
          position: 'bottom' as const,
          layout: { justifyContent: 'flex-start' as const },
          itemMarkerSize: 12,
          itemLabelText: (datum: unknown) => {
            const key = legendKey(datum)
            if (key === 'plan' || key === 'real') {
              return key.charAt(0).toUpperCase() + key.slice(1)
            }
            return key
          },
        },
      },
      interaction: {
        legendFilter: false,
        legendHighlight: false,
      },
      transform: [{ type: 'dodgeX' }],
      axis: {
        x: {
          labelFormatter: (zone: string) => zone,
          labelRender: (datum: { label?: string }) => {
            const zoneKey = datum.label
            if (!zoneKey) return ''
            const d = byZone[zoneKey]
            if (!d) return `<div>${zoneKey}</div>`
            return `<div style="display:flex;flex-direction:column;align-items:center;line-height:1.35;font-size:11px;color:#666">${d.zone}<span style="color:${PLAN_BAR_COLOR}">${d.plan}</span><span>${d.real}</span><span>${d.compliance}%</span></div>`
          },
        },
      },
    }),
    [data, byZone],
  )

  return (
    <>
      <ChartTitle level={5}>
        {`${t(
          'admin_router.dashboard.monthly_compliance_by_zone_chart_title',
        )} ${formatMonthYearLabel(dayjs(selectedMonth))}`}
      </ChartTitle>
      <Spin spinning={isLoading}>
        {hasData ? (
          <Column {...config} />
        ) : (
          <ChartEmptyWrap>
            <Empty description={t('admin_router.dashboard.monthly_compliance_by_zone_empty')} />
          </ChartEmptyWrap>
        )}
      </Spin>
    </>
  )
}
